import { CSSProperties, ReactNode } from 'react';

interface LinkProps {
  href: string;
  className?: string;
  children: ReactNode;
  target?: string;
  rel?: string;
  style?: CSSProperties;
  'aria-label'?: string;
}

/**
 * Internal navigation goes through the URL hash so refresh works on
 * shared hosts where .htaccess rewriting isn't honored.
 *
 *   <Link href="/bedum">  → sets window.location.hash = "/bedum"
 *
 * External links and mailto: pass through to a plain anchor.
 */
export const Link = ({
  href,
  className,
  children,
  target,
  rel,
  style,
  'aria-label': ariaLabel,
}: LinkProps) => {
  const isExternal =
    href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:');

  if (isExternal || target === '_blank') {
    return (
      <a
        href={href}
        className={className}
        style={style}
        target={target}
        rel={rel || (target === '_blank' ? 'noopener noreferrer' : undefined)}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }

  // In-page anchor (e.g. "#info") — let the browser handle natively
  if (href.startsWith('#')) {
    return (
      <a href={href} className={className} style={style} aria-label={ariaLabel}>
        {children}
      </a>
    );
  }

  // Internal route — App.tsx's global click router intercepts and routes
  // through the hash. We still render an <a href="/foo"> so right-click /
  // copy-link works (the user can paste it as /#/foo if they edit, or land
  // on the homepage and be redirected via 404.html otherwise).
  return (
    <a href={href} className={className} style={style} aria-label={ariaLabel}>
      {children}
    </a>
  );
};
